import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Sparkles, Palette, Video, Package, Check, Gift, Keyboard, MessageCircle, FileImage, FileText, Droplets, Type, Layers, Lightbulb, RefreshCw, CheckCircle, LockOpen, Award, ArrowLeft, ArrowRight, Download, Copy, Loader2, LogIn, LogOut, ChevronDown, Folder, Bot, Zap, Quote, Building2, ExternalLink } from "lucide-react";
import { ColorSwatch } from "@/components/ColorSwatch";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import BlackFridayBanner from "@/components/BlackFridayBanner";
import { SocialProof } from "@/components/SocialProof";
import { LogoGallery } from "@/components/LogoGallery";
import { FAQ } from "@/components/FAQ";
import Footer from "@/components/Footer";
import { LoadingMessages } from "@/components/LoadingMessages";
import { SignupModal } from "@/components/SignupModal";
import { PaywallModal } from "@/components/PaywallModal";
import HardPaywallModal from "@/components/HardPaywallModal";
import { LogoApprovalStep } from "@/components/LogoApprovalStep";
import { useGuestGeneration } from "@/hooks/useGuestGeneration";
import { useWizardPersistence } from "@/hooks/useWizardPersistence";
import { useDynamicFont } from "@/hooks/useDynamicFont";
import logoKitMarca from "@/assets/logo-kitmarca.png";
import brandGuidelinesPreview from "@/assets/showcase/brand-guidelines-preview.webp";
// Hero image removed - clean text + form layout
import mascotesPreview from "@/assets/showcase/mascotes-preview.webp";
import { trackTikTokViewContent } from "@/lib/tiktok-tracking";
import { trackLead, trackInitiateCheckout } from "@/lib/unified-tracking";
import { useUtmCapture } from "@/hooks/useUtmTracking";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { usePurchaseStatus } from "@/hooks/usePurchaseStatus";
import { useAuth } from "@/contexts/AuthContext";
import { getFontDownloadLink } from "@/lib/fontOptions";

// Wizard Data
const SEGMENTS = [
  "Beleza e Estética",
  "Vestuário e Moda",
  "Construção Civil",
  "Alimentação e Bebidas",
  "Serviços Domésticos",
  "Transporte e Logística",
  "Mercados e Mercearias",
  "Educação e Treinamentos",
  "Marketing e Digital",
  "Artesanato e Manufatura",
  "Tecnologia",
  "Saúde e Bem-estar",
  "Imóveis",
  "Finanças",
  "Entretenimento",
  "Serviços Gerais",
];

const PERSONALITIES = [
  { id: "moderno", title: "Moderno", subtitle: "Limpo e geométrico", fontFamily: "'Montserrat', sans-serif", fontWeight: 500 },
  { id: "classico", title: "Clássico", subtitle: "Atemporal e refinado", fontFamily: "'Playfair Display', serif", fontWeight: 500 },
  { id: "divertido", title: "Divertido", subtitle: "Alegre e arredondado", fontFamily: "'Pally', sans-serif", fontWeight: 700 },
  { id: "luxuoso", title: "Luxuoso", subtitle: "Elegante e premium", fontFamily: "'Playfair Display', serif", fontWeight: 700 },
  { id: "tech", title: "Tech", subtitle: "Futurista e afiado", fontFamily: "'Switzer', sans-serif", fontWeight: 500 },
  { id: "rustico", title: "Rústico", subtitle: "Artesanal e orgânico", fontFamily: "'Josefin Slab', serif", fontWeight: 500 },
  { id: "minimalista", title: "Minimalista", subtitle: "Arejado e essencial", fontFamily: "'Satoshi', sans-serif", fontWeight: 300 },
  { id: "forte", title: "Forte", subtitle: "Impactante e pesado", fontFamily: "'Clash Display', sans-serif", fontWeight: 700 },
  { id: "criativo", title: "Criativo", subtitle: "Artístico e único", fontFamily: "'Abril Fatface', serif", fontWeight: 400 },
];

const COLORS = [
  { id: "branco", name: "Branco", hex: "#FFFFFF", border: true },
  { id: "amarelo", name: "Amarelo", hex: "#FACC15" },
  { id: "laranja", name: "Laranja", hex: "#F97316" },
  { id: "vermelho", name: "Vermelho", hex: "#EF4444" },
  { id: "verde", name: "Verde", hex: "#22C55E" },
  { id: "azul", name: "Azul", hex: "#3B82F6" },
  { id: "rosa", name: "Rosa", hex: "#EC4899" },
  { id: "roxo", name: "Roxo", hex: "#A855F7" },
  { id: "violeta", name: "Violeta", hex: "#8B5CF6" },
  { id: "preto", name: "Preto", hex: "#1F2937" },
  { id: "cinza", name: "Cinza", hex: "#6B7280" },
  { id: "marrom", name: "Marrom", hex: "#A16207" },
];

const SYMBOL_CONCEPTS = [
  { id: "monograma", title: "Monograma Elegante", description: "Letras iniciais estilizadas" },
  { id: "abstrato", title: "Ícone Abstrato", description: "Formas únicas e artísticas" },
  { id: "literal", title: "Elemento Literal", description: "Ex: Bolo, Casa, Tesoura" },
  { id: "tipografico", title: "Tipográfico Minimalista", description: "Foco no nome da marca" },
  { id: "figura-iconica", title: "Figura Icônica", description: "Personagem ou personalidade que representa a marca" },
  { id: "emblema", title: "Emblema/Brasão", description: "Selo ou escudo clássico" },
];

interface FormData {
  brandName: string;
  slogan: string;
  segment: string;
  description: string;
  personality: string;
  color: string;
  symbolConcept: string;
  conceptRefinement: string;
}

const Index = () => {
  // Capture UTM params on page load
  useUtmCapture();
  const navigate = useNavigate();

  // Guest generation tracking (for paywall)
  const { hasGeneratedPreview, markGenerated, getSessionId } = useGuestGeneration();

  // Wizard persistence (for OAuth redirect protection)
  const { 
    saveWizardState, 
    restoreWizardState, 
    clearWizardState, 
    markOAuthPending, 
    wasOAuthPending, 
    clearOAuthPending 
  } = useWizardPersistence();

  // Track if we've already restored state (prevent double restore)
  const hasRestoredRef = useRef(false);

  // Generation limits
  const MAX_GENERATIONS = 10; // 1 initial + 9 variations
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationCount, setGenerationCount] = useState(0);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showHardPaywall, setShowHardPaywall] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<ReturnType<typeof getLogoVariations>[0] | null>(null);
  
  // NEW: Approval Step states
  const [rawLogoUrl, setRawLogoUrl] = useState<string | null>(null);
  const [pendingApprovalAction, setPendingApprovalAction] = useState<'approve' | 'regenerate' | null>(null);
  const [isFinalizingBrand, setIsFinalizingBrand] = useState(false);
  
  // AI Suggestion state for Step 4
  const [aiSuggestion, setAiSuggestion] = useState<string>('');
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  const [lastSuggestedConcept, setLastSuggestedConcept] = useState<string>('');
  
  const [formData, setFormData] = useState<FormData>({
    brandName: "",
    slogan: "",
    segment: "",
    description: "",
    personality: "",
    color: "",
    symbolConcept: "",
    conceptRefinement: "",
  });

  const totalSteps = 6; // 1-4 wizard, 5 approval, 6 result

  // Auth from global context (handles OAuth persistence)
  const { session, user, signOut, isAuthReady } = useAuth();
  const isCheckingAuth = !isAuthReady; // Alias for existing UI code

  // Purchase status hook - checks if user has paid
  const { hasPurchased, isLoading: purchaseLoading, refetchStatus } = usePurchaseStatus('brand_kit');

  // =============================================================
  // WIZARD STATE RESTORATION (handles OAuth redirect return)
  // =============================================================
  useEffect(() => {
    // GATE 1: Wait for auth to be ready before processing
    if (!isAuthReady) return;
    
    // GATE 2: Only run once
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const savedState = restoreWizardState();
    const oauthWasPending = wasOAuthPending();

    if (savedState) {
      console.log('[WizardRestore] Found saved state at step', savedState.currentStep);
      
      // Restore form data (always, regardless of auth)
      setFormData(savedState.formData);
      setCurrentStep(savedState.currentStep);
      setGenerationCount(savedState.generationCount || 0);
      
      // Restore generated brand if it exists
      if (savedState.generatedBrand) {
        setGeneratedBrand(savedState.generatedBrand);
      }

      // GATE 3: Only show success toast if returning from OAuth AND session exists
      if (oauthWasPending) {
        clearOAuthPending();
        
        if (session) {
          // ✅ Auth confirmed - show success
          toast.success('Sua marca foi restaurada!');
        } else {
          // ⚠️ OAuth was initiated but no session - show warning
          console.warn('[WizardRestore] OAuth was pending but no session established');
          toast.error('Sessão não estabelecida. Faça login novamente.');
        }
      }
    } else if (oauthWasPending) {
      // Clear stale OAuth flag even if no saved state
      clearOAuthPending();
    }
  }, [isAuthReady, session, restoreWizardState, wasOAuthPending, clearOAuthPending]);

  // =============================================================
  // AUTO-SAVE WIZARD STATE (protects against accidental loss)
  // =============================================================
  useEffect(() => {
    // Only save if user has started the wizard (has brand name or moved past step 1)
    if (currentStep > 1 || formData.brandName.trim() !== '') {
      // Need to read generatedBrand after it's defined, so we use a callback pattern
      const timeoutId = setTimeout(() => {
        // Get current generatedBrand value (will be null initially)
        const currentGeneratedBrand = (window as any).__clicklogos_generatedBrand || null;
        saveWizardState(formData, currentStep, currentGeneratedBrand, generationCount);
      }, 500); // Debounce to avoid excessive saves
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentStep, formData, generationCount, saveWizardState]);

  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));
    
    return () => observer.disconnect();
  }, [currentStep]);

  // Logout function
  const handleLogout = async () => {
    await signOut();
    
    // Reset wizard state completely
    setCurrentStep(1);
    setGeneratedBrand(null);
    setFormData({
      brandName: '',
      slogan: '',
      segment: '',
      description: '',
      personality: '',
      color: '',
      symbolConcept: '',
      conceptRefinement: '',
    });
    clearWizardState();
    
    toast.success("Logout realizado com sucesso!");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset wizard to step 1
  const handleResetWizard = () => {
    setCurrentStep(1);
    setGeneratedBrand(null);
    setFormData({
      brandName: '',
      slogan: '',
      segment: '',
      description: '',
      personality: '',
      color: '',
      symbolConcept: '',
      conceptRefinement: '',
    });
    clearWizardState();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Track ViewContent when landing page loads
  useEffect(() => {
    trackTikTokViewContent({
      content_id: 'clicklogos-completo',
      content_name: 'ClickLogos Completo',
      content_type: 'product',
      value: 100,
      currency: 'BRL',
      brand: 'ClickLogos'
    });
  }, []);

  const updateFormData = (key: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.brandName.trim() !== "" && formData.segment !== "" && formData.description.trim() !== "";
      case 2:
        return formData.personality !== "";
      case 3:
        return formData.color !== "";
      case 4:
        return formData.symbolConcept !== "";
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
    } else if (currentStep === 4) {
      // 🔐 STRICT GATE: Check if user already generated once (and hasn't paid)
      if (hasGeneratedPreview() && !hasPurchased) {
        trackInitiateCheckout({ value: 89.90 });
        setShowHardPaywall(true);
        return; // BLOCK execution
      }

      // 🔐 PAID USER LIMIT: Check if paid user has exceeded 10 generations
      if (hasPurchased && generationCount >= MAX_GENERATIONS) {
        toast.error(`Limite de ${MAX_GENERATIONS} gerações atingido. Entre em contato para mais.`);
        return; // BLOCK execution
      }

      setIsGenerating(true);
      try {
        const selectedColor = COLORS.find(c => c.id === formData.color);
        
        // Track Lead - user is generating brand (Meta + TikTok)
        trackLead(`${formData.brandName} - ${formData.segment}`);
        
        // 🆕 COST OPTIMIZATION: Call ONLY generate-raw-logo (no background removal yet)
        const response = await supabase.functions.invoke('generate-raw-logo', {
          body: {
            brandName: formData.brandName,
            slogan: formData.slogan,
            segment: formData.segment,
            description: formData.description,
            personality: formData.personality,
            color: selectedColor?.hex || "#10B981",
            symbolConcept: formData.symbolConcept,
          },
          headers: {
            'x-session-id': getSessionId(),
          }
        });

        if (response.error) {
          throw new Error(response.error.message);
        }

        // Handle rate limit from backend
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Falha ao gerar logo');
        }

        // Store raw logo URL for approval step
        setRawLogoUrl(response.data.rawImageUrl);
        
        // Mark as generated for paywall tracking
        markGenerated(response.data.rawImageUrl || '', response.data);
        
        // Increment generation counter
        setGenerationCount(prev => prev + 1);

        // 🆕 Go to APPROVAL STEP (Step 5), NOT results
        setCurrentStep(5);
      } catch (error) {
        console.error('Error generating logo:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage.includes('Rate limit')) {
          toast.error("Limite de requisições atingido. Aguarde um momento.");
          return;
        }
        toast.error("Erro ao gerar logo. Tente novamente.");
      } finally {
        setIsGenerating(false);
      }
    }
  };

  // 🆕 APPROVAL STEP: Handle approve/regenerate actions with paywall
  const handleApprovalAction = async (action: 'approve' | 'regenerate') => {
    // BARREIRA 1: Login obrigatório
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      setPendingApprovalAction(action);
      setShowSignupModal(true);
      return;
    }

    // BARREIRA 2: Verificar admin bypass OU créditos
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', currentUser.id)
      .eq('role', 'admin')
      .maybeSingle();

    const isAdmin = !!roleData;

    if (!isAdmin) {
      // Não é admin - verificar pagamento/créditos
      if (!hasPurchased) {
        trackInitiateCheckout({ value: 89.90 });
        setPendingApprovalAction(action);
        setShowPaywall(true);
        return;
      }
    }

    // ✅ PASSOU TODAS AS BARREIRAS - executar ação
    if (action === 'approve') {
      await executeFinalization();
    } else {
      await executeRegeneration();
    }
  };

  // 🆕 FINALIZE: Call expensive operations ONLY after payment verification
  const executeFinalization = async () => {
    if (!rawLogoUrl) {
      toast.error("Nenhuma logo para finalizar");
      return;
    }

    setIsFinalizingBrand(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        toast.error("Sessão expirada. Faça login novamente.");
        setShowSignupModal(true);
        return;
      }

      const selectedColor = COLORS.find(c => c.id === formData.color);
      
      // 🔥 EXPENSIVE OPERATION: Background removal + fonts + palette + copywriting
      const response = await supabase.functions.invoke('finalize-branding', {
        body: {
          rawImageUrl: rawLogoUrl,
          brandData: {
            brandName: formData.brandName,
            slogan: formData.slogan,
            segment: formData.segment,
            description: formData.description,
            personality: formData.personality,
            color: selectedColor?.hex || "#10B981",
            symbolConcept: formData.symbolConcept,
          }
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (!response.data?.success) {
        // Handle NO_CREDITS error
        if (response.data?.error === 'NO_CREDITS') {
          toast.error("Você não tem créditos disponíveis");
          setShowPaywall(true);
          return;
        }
        throw new Error(response.data?.error || 'Falha ao finalizar marca');
      }

      // ✅ SUCCESS: Set brand data and go to results
      setGeneratedBrand(response.data);
      setCurrentStep(6); // Go to RESULTS (Step 6)
      
      toast.success("Identidade visual criada com sucesso!");
    } catch (error) {
      console.error('Error finalizing brand:', error);
      toast.error("Erro ao criar identidade visual. Tente novamente.");
    } finally {
      setIsFinalizingBrand(false);
    }
  };

  // 🆕 REGENERATE: Generate new logo variation
  const executeRegeneration = async () => {
    // Check generation limits
    if (generationCount >= MAX_GENERATIONS) {
      toast.error(`Limite de ${MAX_GENERATIONS} gerações atingido.`);
      return;
    }

    setIsGenerating(true);
    try {
      const selectedColor = COLORS.find(c => c.id === formData.color);
      
      const response = await supabase.functions.invoke('generate-raw-logo', {
        body: {
          brandName: formData.brandName,
          slogan: formData.slogan,
          segment: formData.segment,
          description: formData.description,
          personality: formData.personality,
          color: selectedColor?.hex || "#10B981",
          symbolConcept: formData.symbolConcept,
        },
        headers: {
          'x-session-id': getSessionId(),
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Falha ao gerar nova logo');
      }

      // Update raw logo URL
      setRawLogoUrl(response.data.rawImageUrl);
      setGenerationCount(prev => prev + 1);
      
      toast.success("Nova opção gerada!");
    } catch (error) {
      console.error('Error regenerating logo:', error);
      toast.error("Erro ao gerar nova opção. Tente novamente.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle login from hard paywall
  const handleLoginFromHardPaywall = () => {
    setShowHardPaywall(false);
    setShowSignupModal(true);
  };

  // Scroll to top when reaching Step 5 (approval) or Step 6 (results)
  useEffect(() => {
    if (currentStep === 5 || currentStep === 6) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentStep]);

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Logo variations array - uses PREVIEW URLs (with burned watermark) for display
  const getLogoVariations = () => [
    {
      id: 'colorida',
      label: 'Colorida',
      previewUrl: variations['colorida']?.previewUrl || transparentUrl || generatedBrand?.logo?.url,
      cleanUrl: variations['colorida']?.cleanUrl || transparentUrl || generatedBrand?.logo?.url,
      bgColor: undefined,
      checkerboard: true,
      filter: 'none' as const,
    },
    {
      id: 'preto',
      label: 'Preto 100%',
      previewUrl: variations['preto']?.previewUrl || silhouetteUrl || generatedBrand?.logo?.url,
      cleanUrl: variations['preto']?.cleanUrl || silhouetteUrl || generatedBrand?.logo?.url,
      bgColor: undefined,
      checkerboard: true,
      filter: 'none' as const,
    },
    {
      id: 'neutro-claro',
      label: 'Neutro Claro',
      previewUrl: variations['neutro-claro']?.previewUrl || darkLogoUrl || generatedBrand?.logo?.url,
      cleanUrl: variations['neutro-claro']?.cleanUrl || darkLogoUrl || generatedBrand?.logo?.url,
      bgColor: generatedBrand?.colorPalette?.[2]?.hex || '#F8F9FA',
      checkerboard: false,
      filter: 'none' as const,
    },
    {
      id: 'tons-neutros',
      label: 'Tons Neutros',
      previewUrl: variations['tons-neutros']?.previewUrl || invertedUrl || generatedBrand?.logo?.url,
      cleanUrl: variations['tons-neutros']?.cleanUrl || invertedUrl || generatedBrand?.logo?.url,
      bgColor: generatedBrand?.colorPalette?.[3]?.hex || '#1F2937',
      checkerboard: false,
      filter: 'none' as const,
    },
  ];

  // SECURITY: Execute actual download using CLEAN URL (no watermark)
  const executeActualDownload = (cleanUrl?: string, name?: string, bgColor?: string) => {
    if (!cleanUrl) {
      toast.error("Nenhuma logo para baixar");
      return;
    }
    downloadProcessedVariation(cleanUrl, name || 'logo', 'none', bgColor);
  };

  // Handle download for specific variation - uses CLEAN URL after payment
  const handleVariationDownload = (variation: ReturnType<typeof getLogoVariations>[0]) => {
    // 1. Check if user is logged in
    if (!session || !user) {
      setPendingDownload(variation);
      setShowSignupModal(true);
      return;
    }
    
    // 2. Check if user has PAID (crucial!)
    if (!hasPurchased) {
      // Track InitiateCheckout (Meta + TikTok)
      trackInitiateCheckout({ value: 89.90 });
      setPendingDownload(variation);
      setShowPaywall(true);
      return;
    }
    
    // 3. If paid, execute download with CLEAN URL (no watermark!)
    executeActualDownload(variation.cleanUrl, variation.id, variation.bgColor);
  };

  // Handle main hero download - uses CLEAN URL
  const handleDownload = () => {
    // 1. Check if user is logged in
    if (!session || !user) {
      setPendingDownload(null); // Hero logo, not a variation
      setShowSignupModal(true);
      return;
    }
    
    // 2. Check if user has PAID
    if (!hasPurchased) {
      // Track InitiateCheckout (Meta + TikTok)
      trackInitiateCheckout({ value: 89.90 });
      setPendingDownload(null);
      setShowPaywall(true);
      return;
    }
    
    // 3. If paid, execute download using CLEAN URL
    executeActualDownload(heroCleanUrl || generatedBrand?.logo?.url, 'logo');
  };

  // Handle cross-sell: Claim free mascot for brand kit purchasers
  const handleClaimFreeMascot = async () => {
    // 1. Check if user is logged in
    if (!session?.user) {
      toast.error("Faça login para resgatar seu mascote grátis");
      setShowSignupModal(true);
      return;
    }

    // 2. Check if user has purchased brand_kit
    if (!hasPurchased) {
      toast.error("Você precisa ter comprado o Kit Marca para ganhar o mascote");
      return;
    }

    try {
      // 3. Check if user already has mascot access
      const { data: existingMascot } = await supabase
        .from('purchases')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('product_type', 'mascot')
        .maybeSingle();

      if (existingMascot) {
        // Already has mascot - just redirect
        toast.success("Você já tem acesso ao mascote!");
        navigate("/mascote-generator");
        return;
      }

      // 4. Get order_id from brand_kit purchase to link
      const { data: brandKitPurchase } = await supabase
        .from('purchases')
        .select('order_id')
        .eq('user_id', session.user.id)
        .eq('product_type', 'brand_kit')
        .maybeSingle();

      // 5. Insert free mascot purchase (valid for 30 days)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      const { error } = await supabase
        .from('purchases')
        .insert({
          user_id: session.user.id,
          product_type: 'mascot',
          order_id: brandKitPurchase?.order_id || crypto.randomUUID(),
          expires_at: expiresAt.toISOString(),
        });

      if (error) {
        console.error('Error granting free mascot:', error);
        toast.error("Erro ao resgatar mascote. Tente novamente.");
        return;
      }

      // 6. Success - redirect
      toast.success("🎁 Mascote grátis liberado! Vamos criar?");
      navigate("/mascote-generator");

    } catch (error) {
      console.error('Error in handleClaimFreeMascot:', error);
      toast.error("Erro inesperado. Tente novamente.");
    }
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Block Ctrl+S, Ctrl+U, Ctrl+Shift+I
      if (e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
      }
      if (e.ctrlKey && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const copyToClipboard = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label || text} copiado!`);
  };

  // hexToCmyk moved to @/lib/utils

  // Create silhouette image - converts logo pixels to black, keeps background transparent
  const createSilhouetteImage = (
    originalUrl: string,
    onComplete: (silhouetteUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Detect background color from top-left corner
      const bgR = data[0], bgG = data[1], bgB = data[2];
      const bgThreshold = 40; // Color tolerance
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
        
        // If pixel is similar to background OR transparent → make transparent
        const isBg = Math.abs(r - bgR) < bgThreshold && 
                     Math.abs(g - bgG) < bgThreshold && 
                     Math.abs(b - bgB) < bgThreshold;
        
        if (isBg || a < 128) {
          data[i+3] = 0; // Transparent
        } else {
          // Logo pixel → black
          data[i] = 0;
          data[i+1] = 0;
          data[i+2] = 0;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      console.error('Failed to load image for silhouette');
      onComplete(originalUrl); // Fallback to original
    };
    img.src = originalUrl;
  };


  // Download processed variation with canvas
  const downloadProcessedVariation = async (
    originalUrl: string, 
    name: string, 
    filter: 'none' | 'invert' | 'grayscale' | 'opacity' | 'silhouette',
    bgColor?: string
  ) => {
    try {
      console.log('📥 Iniciando download:', { originalUrl, name, filter, bgColor });
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = originalUrl;
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1080;
      const ctx = canvas.getContext('2d')!;
      
      // Draw background if specified
      if (bgColor) {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      // Apply filters
      switch (filter) {
        case 'invert':
          ctx.filter = 'invert(1)';
          break;
        case 'grayscale':
          ctx.filter = 'grayscale(1)';
          break;
        case 'opacity':
          ctx.globalAlpha = 0.3;
          break;
        default:
          break;
      }
      
      // Calculate dimensions to COVER canvas (no padding, no transparent gaps)
      // Use cover logic: scale up to fill, crop excess
      const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const x = (canvas.width - drawWidth) / 2;
      const y = (canvas.height - drawHeight) / 2;
      
      ctx.drawImage(img, x, y, drawWidth, drawHeight);
      
      // Download
      const link = document.createElement('a');
      const dataUrl = canvas.toDataURL('image/png');
      
      console.log('📦 Blob gerado:', { 
        length: dataUrl.length, 
        preview: dataUrl.substring(0, 80) 
      });
      
      if (dataUrl.length < 100) {
        toast.error("Arquivo gerado está vazio ou corrompido!");
        console.error('❌ Arquivo corrompido - muito pequeno');
        return;
      }
      
      link.href = dataUrl;
      link.download = `${formData.brandName}-${name}.png`;
      link.click();
      toast.success(`Download ${name} iniciado!`);
    } catch (error) {
      console.error('❌ Erro no download:', error);
      toast.error("Erro ao processar imagem. Tente novamente.");
    }
  };

  // Generated brand data (from API)
  const [generatedBrand, setGeneratedBrand] = useState<{
    success: boolean;
    logo: { url: string; transparentUrl?: string; prompt: string };
    fontKit: {
      display: { name: string; purpose: string; downloadLink?: string };
      titles: { name: string; purpose: string; downloadLink?: string };
      body: { name: string; purpose: string; downloadLink?: string };
    };
    colorPalette: Array<{ name: string; hex: string; role: string }>;
    copywriting?: {
      tagline: string;
      bio: string;
    };
  } | null>(null);

  // Dynamic font loading - loads fonts from generated brand
  useDynamicFont([
    generatedBrand?.fontKit?.display?.name,
    generatedBrand?.fontKit?.titles?.name,
    generatedBrand?.fontKit?.body?.name,
  ]);

  // Sync generatedBrand to window for auto-save access
  useEffect(() => {
    (window as any).__clicklogos_generatedBrand = generatedBrand;
  }, [generatedBrand]);

  // Handler for OAuth redirect - saves state BEFORE leaving page
  const handleBeforeOAuthRedirect = () => {
    console.log('[OAuth] Saving wizard state before redirect...');
    saveWizardState(formData, currentStep, generatedBrand, generationCount);
    markOAuthPending();
  };

  // State for processed image variations - SECURITY: Clean (original) vs Preview (watermarked)
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | null>(null);
  const [heroCleanUrl, setHeroCleanUrl] = useState<string | null>(null);
  
  // Variations with both preview and clean URLs
  const [variations, setVariations] = useState<{
    [key: string]: { previewUrl: string; cleanUrl: string };
  }>({});

  // Legacy states (being replaced by variations object)
  const [silhouetteUrl, setSilhouetteUrl] = useState<string | null>(null);
  const [invertedUrl, setInvertedUrl] = useState<string | null>(null);
  const [transparentUrl, setTransparentUrl] = useState<string | null>(null);
  const [darkLogoUrl, setDarkLogoUrl] = useState<string | null>(null);

  // SECURITY: Burn watermark permanently into image via Canvas
  const burnWatermark = (
    imageUrl: string,
    onComplete: (previewUrl: string, cleanUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      // 1. Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Save CLEAN version BEFORE adding watermark
      const cleanDataUrl = canvas.toDataURL('image/png');
      
      // 2. Configure watermark text
      ctx.save();
      ctx.globalAlpha = 0.30;
      ctx.font = '600 13px system-ui, sans-serif';
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // 3. Rotation angle (-25 degrees)
      const angle = -25 * Math.PI / 180;
      
      // 4. Calculate grid to fill ENTIRE area (including after rotation)
      const text = 'ClickLogos.com.br';
      const textWidth = 150;
      const textHeight = 60;
      
      // Expand area to cover rotation - use diagonal
      const diagonal = Math.sqrt(canvas.width ** 2 + canvas.height ** 2);
      const startX = -diagonal;
      const startY = -diagonal;
      const endX = diagonal * 2;
      const endY = diagonal * 2;
      
      // Translate to center, rotate, then draw grid
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(angle);
      
      for (let y = startY; y < endY; y += textHeight) {
        for (let x = startX; x < endX; x += textWidth) {
          ctx.fillText(text, x, y);
        }
      }
      
      ctx.restore();
      
      // 5. Preview = image with BURNED watermark
      const previewDataUrl = canvas.toDataURL('image/png');
      
      onComplete(previewDataUrl, cleanDataUrl);
    };
    img.onerror = () => {
      console.error('Failed to load image for watermark burn-in');
      onComplete(imageUrl, imageUrl); // Fallback to original
    };
    img.src = imageUrl;
  };

  // Create transparent image - removes background, keeps logo colors
  const createTransparentImage = (
    originalUrl: string,
    onComplete: (transparentUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Detect background color from top-left corner
      const bgR = data[0], bgG = data[1], bgB = data[2];
      const bgThreshold = 45; // Tolerance
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2];
        
        // If pixel is similar to background → transparent
        const isBg = Math.abs(r - bgR) < bgThreshold && 
                     Math.abs(g - bgG) < bgThreshold && 
                     Math.abs(b - bgB) < bgThreshold;
        
        if (isBg) {
          data[i+3] = 0; // Alpha = 0 (transparent)
        }
        // Otherwise, keep original colors
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(originalUrl);
    img.src = originalUrl;
  };

  // Create dark logo image - paints logo in neutral dark color
  const createDarkLogoImage = (
    originalUrl: string,
    darkColor: string,
    onComplete: (darkLogoUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Parse dark color
      const dr = parseInt(darkColor.slice(1, 3), 16);
      const dg = parseInt(darkColor.slice(3, 5), 16);
      const db = parseInt(darkColor.slice(5, 7), 16);
      
      // Detect background color from top-left corner
      const bgR = data[0], bgG = data[1], bgB = data[2];
      const bgThreshold = 45;
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2];
        
        const isBg = Math.abs(r - bgR) < bgThreshold && 
                     Math.abs(g - bgG) < bgThreshold && 
                     Math.abs(b - bgB) < bgThreshold;
        
        if (isBg) {
          data[i+3] = 0; // Transparent
        } else {
          // Paint logo in dark color
          data[i] = dr;
          data[i+1] = dg;
          data[i+2] = db;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(originalUrl);
    img.src = originalUrl;
  };

  // Create inverted image - converts logo pixels to white, keeps background transparent
  const createInvertedImage = (
    originalUrl: string,
    onComplete: (invertedUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Detect background color from top-left corner
      const bgR = data[0], bgG = data[1], bgB = data[2];
      const bgThreshold = 40;
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
        
        const isBg = Math.abs(r - bgR) < bgThreshold && 
                     Math.abs(g - bgG) < bgThreshold && 
                     Math.abs(b - bgB) < bgThreshold;
        
        if (isBg || a < 128) {
          data[i+3] = 0; // Transparent
        } else {
          // Logo pixel → white (inverted)
          data[i] = 255;
          data[i+1] = 255;
          data[i+2] = 255;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(originalUrl);
    img.src = originalUrl;
  };

  // Helper: Create silhouette from ALREADY TRANSPARENT image
  const createSilhouetteFromTransparent = (
    transparentUrl: string,
    onComplete: (silhouetteUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        // Se pixel tem alpha > 50, pintar de preto puro
        if (data[i+3] > 50) {
          data[i] = 0;     // R
          data[i+1] = 0;   // G
          data[i+2] = 0;   // B
          data[i+3] = 255; // Alpha total
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(transparentUrl);
    img.src = transparentUrl;
  };

  // Helper: Create dark-colored logo from ALREADY TRANSPARENT image
  const createDarkLogoFromTransparent = (
    transparentUrl: string,
    darkColor: string,
    onComplete: (darkLogoUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      const dr = parseInt(darkColor.slice(1, 3), 16);
      const dg = parseInt(darkColor.slice(3, 5), 16);
      const db = parseInt(darkColor.slice(5, 7), 16);
      
      for (let i = 0; i < data.length; i += 4) {
        if (data[i+3] > 50) {
          data[i] = dr;
          data[i+1] = dg;
          data[i+2] = db;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(transparentUrl);
    img.src = transparentUrl;
  };

  // Helper: Create inverted (white) logo from ALREADY TRANSPARENT image
  const createInvertedFromTransparent = (
    transparentUrl: string,
    onComplete: (invertedUrl: string) => void
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        if (data[i+3] > 50) {
          data[i] = 255;
          data[i+1] = 255;
          data[i+2] = 255;
          data[i+3] = 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      onComplete(canvas.toDataURL('image/png'));
    };
    img.onerror = () => onComplete(transparentUrl);
    img.src = transparentUrl;
  };

  // SECURITY: Process all image variations with burn-in watermark
  // Uses transparentUrl from API if available, otherwise falls back to Canvas
  useEffect(() => {
    if (generatedBrand?.logo?.url) {
      // 1. HERO: Burn watermark on original image (with background for visual impact)
      burnWatermark(generatedBrand.logo.url, (preview, clean) => {
        setHeroPreviewUrl(preview);
        setHeroCleanUrl(clean);
      });
      
      // 2. Determine base for variations: API transparent or Canvas fallback
      const processVariationsWithBase = (baseTransparentUrl: string) => {
        console.log('🎨 Processing variations with base:', baseTransparentUrl.substring(0, 80));
        
        // COLORIDA - usa transparent diretamente
        setTransparentUrl(baseTransparentUrl); // Legacy
        burnWatermark(baseTransparentUrl, (preview, clean) => {
          setVariations(prev => ({
            ...prev,
            colorida: { previewUrl: preview, cleanUrl: clean }
          }));
        });
        
        // PRETO 100% - silhouette from transparent
        createSilhouetteFromTransparent(baseTransparentUrl, (silhouetteClean) => {
          setSilhouetteUrl(silhouetteClean); // Legacy
          burnWatermark(silhouetteClean, (preview, clean) => {
            setVariations(prev => ({
              ...prev,
              preto: { previewUrl: preview, cleanUrl: clean }
            }));
          });
        });
        
        // TONS NEUTROS (inverted) from transparent
        createInvertedFromTransparent(baseTransparentUrl, (invertedClean) => {
          setInvertedUrl(invertedClean); // Legacy
          burnWatermark(invertedClean, (preview, clean) => {
            setVariations(prev => ({
              ...prev,
              'tons-neutros': { previewUrl: preview, cleanUrl: clean }
            }));
          });
        });
        
        // NEUTRO CLARO (dark logo) from transparent
        const darkColor = generatedBrand?.colorPalette?.[3]?.hex || '#1F2937';
        createDarkLogoFromTransparent(baseTransparentUrl, darkColor, (darkLogoClean) => {
          setDarkLogoUrl(darkLogoClean); // Legacy
          burnWatermark(darkLogoClean, (preview, clean) => {
            setVariations(prev => ({
              ...prev,
              'neutro-claro': { previewUrl: preview, cleanUrl: clean }
            }));
          });
        });
      };
      
      // Check if API returned transparentUrl
      if (generatedBrand.logo.transparentUrl) {
        console.log('✅ Using professional API transparent image (InSPyReNet)');
        processVariationsWithBase(generatedBrand.logo.transparentUrl);
      } else {
        console.warn('⚠️ Fallback: using Canvas background removal');
        // Fallback: usar método Canvas antigo
        createTransparentImage(generatedBrand.logo.url, (fallbackTransparent) => {
          processVariationsWithBase(fallbackTransparent);
        });
      }
    }
  }, [generatedBrand?.logo?.url, generatedBrand?.logo?.transparentUrl, generatedBrand?.colorPalette]);

  // AI suggestion for Step 4 - contextual based on selected card
  const fetchAiSuggestion = async () => {
    setIsLoadingSuggestion(true);
    try {
      // Find the selected concept to get its title/description
      const selectedConcept = SYMBOL_CONCEPTS.find(c => c.id === formData.symbolConcept);
      
      const response = await supabase.functions.invoke('suggest-symbol-concept', {
        body: {
          brandName: formData.brandName,
          segment: formData.segment,
          description: formData.description,
          // Send selected logo type for contextual suggestion
          logoType: selectedConcept?.title || '',
          logoTypeDescription: selectedConcept?.description || '',
        }
      });
      
      if (response.data?.success && response.data?.suggestion) {
        setAiSuggestion(response.data.suggestion);
        updateFormData('conceptRefinement', response.data.suggestion);
      }
    } catch (error) {
      console.error('Erro ao gerar sugestão:', error);
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  // Auto-load suggestion when entering Step 4 OR when card selection changes
  // PRESERVA edições do usuário: só regenera se card mudou ou campo vazio
  useEffect(() => {
    if (currentStep === 4 && formData.symbolConcept) {
      const cardChanged = formData.symbolConcept !== lastSuggestedConcept;
      const fieldEmpty = !formData.conceptRefinement || formData.conceptRefinement.trim() === '';
      
      if (fieldEmpty || cardChanged) {
        setLastSuggestedConcept(formData.symbolConcept);
        fetchAiSuggestion();
      }
    }
  }, [currentStep, formData.symbolConcept]);


  // Legacy content data
  const features = [{
    icon: Palette,
    title: "Mascote Único",
    description: "Crie um personagem exclusivo que representa sua marca"
  }, {
    icon: Video,
    title: "Vídeos Animados",
    description: "Gere vídeos curtos com narração para redes sociais"
  }, {
    icon: Package,
    title: "Kit Marca Profissional",
    description: "Logo, cores, fontes e mensagens prontas para usar"
  }];

  const howItWorksSteps = [{
    icon: Keyboard,
    title: "1) Digite o nome da empresa",
    description: "Comece pelo básico. Nada de cadastros longos, só o nome e pronto."
  }, {
    icon: Lightbulb,
    title: "2) Defina seu estilo",
    description: "Selecione o contexto do seu negócio ou personalize o visual do seu jeito."
  }, {
    icon: Droplets,
    title: "3) Selecione a paleta de cores",
    description: "Selecione as cores que vão destacar sua marca e atrair clientes."
  }, {
    icon: Type,
    title: "4) Selecione a tipografia",
    description: "Selecione a fonte principal que dará a voz e o tom da sua marca."
  }, {
    icon: RefreshCw,
    title: "5) Refine até amar",
    description: "Não gostou de primeira? Você pode recriar e ajustar a mesma marca até 10x."
  }, {
    icon: CheckCircle,
    title: "6) Pronto!",
    description: "Tudo pronto para comunicar melhor o seu negócio e vender mais."
  }];

  const whatYouGetItems = [{
    icon: Sparkles,
    title: "Logo Profissional Completa",
    description: "Símbolo único + nome da sua empresa, com design equilibrado e pronto para uso."
  }, {
    icon: Droplets,
    title: "Paleta de Cores Oficial",
    description: "Receba os códigos exatos (HEX) para manter o padrão em impressos e na internet."
  }, {
    icon: Type,
    title: "Fontes Prontas para Baixar",
    description: "Link direto para baixar e instalar a tipografia oficial da sua marca gratuitamente."
  }, {
    icon: Layers,
    title: "PNG em Alta Definição",
    description: "Arquivo de qualidade máxima, ideal para fachadas, banners e grandes impressões."
  }, {
    icon: FileImage,
    title: "Logo com Fundo Transparente",
    description: "Variações prontas para aplicar sobre fotos, Instagram, WhatsApp e cardápios digitais."
  }, {
    icon: FileText,
    title: "Kit Resumo da Marca",
    description: "Sua identidade visual completa condensada em uma única página: logo, cores, fontes e variações."
  }];

  const trustPoints = [{
    icon: MessageCircle,
    title: "Suporte Humano no WhatsApp",
    description: "Dúvidas? Fale com gente de verdade. Nossa equipe analisa e responde suas solicitações pessoalmente."
  }, {
    icon: LockOpen,
    title: "Pagou, Baixou. Sem Assinatura.",
    description: "Livre-se das mensalidades. Aqui você paga uma única vez e os arquivos são seus para sempre."
  }, {
    icon: Award,
    title: "Criado por Especialistas do Brasil",
    description: "Ferramenta desenvolvida por Designer de Marcas com mais de 10 anos de mercado."
  }];

  const plans = [{
    name: "Mascote Oficial",
    price: "R$ 29,90",
    originalPrice: "R$ 89,90",
    discount: "67% OFF",
    period: "único",
    available: true,
    badge: "CYBER WEEK 🚀",
    features: ["3 variações de mascote geradas por IA", "Arquivos em Alta Resolução (PNG)", "Versão com fundo transparente", "Uso comercial liberado"],
    buttonText: "Criar Mascote Agora"
  }, {
    name: "Kit Marca Completo",
    price: "R$ 89,90",
    originalPrice: "R$ 299,90",
    discount: "70% OFF - Oferta de Lançamento",
    period: "único",
    popular: true,
    available: true,
    badge: "CYBER WEEK 🚀",
    features: ["Logo Profissional (Símbolo + Texto)", "Arquivos PNG (Fundo transparente e alta def.)", "Paleta de Cores Oficial (Códigos Hex)", "Tipografia (Nome das fontes para download)", "Uso comercial liberado"],
    bonus: "🎁 BÔNUS EXCLUSIVO: Inclui 1 Mascote Oficial grátis!",
    buttonText: "Começar Agora"
  }, {
    name: "Vídeos Animados",
    price: "Em breve",
    period: "aguarde",
    available: false,
    features: ["Mascote + Animação", "Voz Sintética em PT-BR", "Roteiros automáticos", "Música licenciada"],
    buttonText: "Quero ser avisado"
  }];

  return (
    <main className="min-h-screen">
      <BlackFridayBanner />
      
      {/* Wizard Section - THE APP */}
      <section className="relative bg-gradient-hero min-h-screen py-6 sm:py-8 md:py-12 px-5 sm:px-6">
        <div className="container mx-auto relative z-10">
          {/* Header with Logo and Auth */}
          <div className="flex items-center justify-between mb-4 sm:mb-6 h-14 sm:h-16">
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleResetWizard}
              role="button"
              aria-label="Voltar ao início"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
                <img 
                  src="/logo-kitmarca.png"
                  alt="ClickLogos" 
                  className="w-full h-full object-contain"
                  fetchPriority="high"
                  width={40}
                  height={40}
                />
              </div>
              <span className="text-white font-bold text-base sm:text-lg hidden sm:block">ClickLogos</span>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {currentStep > 1 && (
                <span className="text-white/80 text-xs sm:text-sm font-medium hidden sm:inline">
                  Passo {currentStep} de {totalSteps}
                </span>
              )}
              
              {/* Auth State - Show skeleton while checking */}
              {isCheckingAuth ? (
                <Skeleton className="w-8 h-8 rounded-full bg-white/20" />
              ) : session ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 gap-2">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                          {user?.email?.charAt(0).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      <Folder className="w-4 h-4 mr-2" />
                      Meus Projetos
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowSignupModal(true)}
                  className="text-white hover:bg-white/10"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Entrar
                </Button>
              )}
            </div>
          </div>

          {/* Progress Bar - Reserve space to prevent layout shift */}
          <div className="w-full h-1.5 mb-8">
            {currentStep > 1 && (
              <div className="w-full bg-white/20 rounded-full h-full">
                <div
                  className="h-full bg-white rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            )}
          </div>

          {/* Step 1: Data Entry - THE HOME */}
          {currentStep === 1 && (
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left Column: Text */}
              <div className="text-center lg:text-left order-1 lg:order-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-display font-bold text-white mb-3 sm:mb-4 text-balance leading-snug tracking-tight opacity-0 animate-fade-up break-words hyphens-auto">
                  Sua marca profissional pronta<br className="hidden sm:block" /><span className="sm:hidden"> </span>antes do café esfriar
                </h1>
                <p className="text-white/80 text-sm sm:text-base lg:text-lg mb-6 lg:mb-8 text-balance leading-relaxed opacity-0 animate-fade-up delay-200">
                  Sem agências caras, sem reuniões intermináveis. Identidade visual completa com a qualidade que seu negócio merece.
                </p>
                
                {/* Bullet points de benefícios - Desktop only */}
                <div className="hidden lg:flex flex-col gap-3 opacity-0 animate-fade-up delay-400">
                  <div className="flex items-center gap-3 text-white/90">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-base">Logo profissional em minutos</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/90">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-base">Paleta de cores + tipografia</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/90">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-base">Mascote exclusivo para sua marca</span>
                  </div>
                </div>
              </div>

              {/* Right Column: Form Card */}
              <div className="order-2 lg:order-2 opacity-0 animate-fade-up delay-300">
                <Card className="p-5 sm:p-6 md:p-8 space-y-5 bg-white shadow-2xl shadow-black/10 border-0">
                  {/* 1. Nome da Marca */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground/80 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Nome da Marca
                    </label>
                    <Input
                      placeholder="Ex: Doce Sabor"
                      value={formData.brandName}
                      onChange={(e) => updateFormData("brandName", e.target.value)}
                      className="text-xl md:text-2xl h-14 md:h-16 font-semibold border-2 focus:border-primary"
                      autoFocus
                    />
                  </div>

                  {/* 2. Slogan (Opcional) */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground/80 flex items-center gap-2">
                      <Quote className="w-4 h-4 text-muted-foreground" />
                      Slogan <span className="text-xs text-muted-foreground/60">(Opcional)</span>
                    </label>
                    <Input
                      placeholder="Ex: Feito com amor, entregue com carinho"
                      value={formData.slogan}
                      onChange={(e) => updateFormData("slogan", e.target.value)}
                      className="h-11"
                    />
                  </div>

                  {/* 3. Segmento */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground/80 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      Segmento do Negócio
                    </label>
                    <Select value={formData.segment} onValueChange={(value) => updateFormData("segment", value)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Selecione o segmento" />
                      </SelectTrigger>
                      <SelectContent>
                        {SEGMENTS.map((segment) => (
                          <SelectItem key={segment} value={segment}>
                            {segment}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* 4. Descrição */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground/80 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      Descrição do Negócio
                    </label>
                    <Textarea
                      placeholder="Ex: Faço bolos caseiros com receitas da avó para festas e eventos..."
                      value={formData.description}
                      onChange={(e) => updateFormData("description", e.target.value)}
                      className="min-h-[100px] resize-none"
                    />
                  </div>

                  <Button 
                    size="lg" 
                    className="w-full h-14 text-lg animate-pulse-subtle hover:animate-none"
                    onClick={handleNext}
                    disabled={!canProceed()}
                  >
                    Criar Minha Marca
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Card>
              </div>
            </div>
          )}

          {/* Step 2: Personality */}
          {currentStep === 2 && (
            <div className="max-w-6xl mx-auto animate-fade-in px-6 sm:px-8">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white mb-2 sm:mb-3 text-balance leading-snug tracking-tight break-words">
                  Qual a personalidade da marca?
                </h2>
                <p className="text-white/80 text-sm sm:text-base lg:text-lg text-balance leading-relaxed">
                  O estilo define como as pessoas enxergam sua marca.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {PERSONALITIES.map((p) => (
                  <Card
                    key={p.id}
                    onClick={() => updateFormData("personality", p.id)}
                    className={cn(
                      "p-3 sm:p-5 md:p-6 cursor-pointer transition-all duration-300 ease-out hover:shadow-lg active:scale-[0.98]",
                      formData.personality === p.id
                        ? "bg-primary ring-2 ring-primary ring-offset-2 ring-offset-background scale-[1.02] shadow-lg shadow-primary/25"
                        : "bg-white/95 backdrop-blur hover:border-primary/50 hover:scale-[1.01]"
                    )}
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className={cn(
                        "w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ease-out",
                        formData.personality === p.id 
                          ? "border-white bg-white scale-110" 
                          : "border-muted-foreground/30 scale-100"
                      )}>
                        <Check className={cn(
                          "w-3 h-3 sm:w-4 sm:h-4 text-primary transition-all duration-300 ease-out",
                          formData.personality === p.id 
                            ? "opacity-100 scale-100" 
                            : "opacity-0 scale-50"
                        )} />
                      </div>
                      <div className="min-w-0">
                        <h3 
                          className={cn(
                            "text-base sm:text-xl md:text-2xl truncate transition-colors duration-300",
                            formData.personality === p.id ? "text-white font-bold" : "text-foreground"
                          )}
                          style={{ fontFamily: p.fontFamily, fontWeight: p.fontWeight }}
                        >
                          {p.title}
                        </h3>
                        <p className={cn(
                          "text-sm hidden sm:block transition-colors duration-300",
                          formData.personality === p.id ? "text-white/80" : "text-muted-foreground"
                        )}>{p.subtitle}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={handleBack} className="text-white hover:bg-white/20">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <Button onClick={handleNext} disabled={!canProceed()} className="bg-white text-primary hover:bg-white/90">
                  Próximo Passo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Colors */}
          {currentStep === 3 && (
            <div className="max-w-6xl mx-auto animate-fade-in px-6 sm:px-8">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white mb-2 sm:mb-3 text-balance leading-snug tracking-tight break-words">
                  Escolha uma Vibe
                </h2>
                <p className="text-white/80 text-sm sm:text-base lg:text-lg text-balance leading-relaxed">
                  Qual a cor principal da sua marca?
                </p>
              </div>

              <Card className="p-5 sm:p-8 md:p-12 bg-white/95 backdrop-blur">
                <div className="flex flex-wrap justify-center gap-3 sm:gap-6 md:gap-8">
                  {COLORS.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => updateFormData("color", color.id)}
                      className={cn(
                        "rounded-full transition-all duration-200 relative w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 shadow-md hover:shadow-lg",
                        color.border && "border-2 border-muted",
                        formData.color === color.id
                          ? "ring-4 ring-primary ring-offset-2 sm:ring-offset-4 scale-110"
                          : "hover:scale-110"
                      )}
                      style={{ backgroundColor: color.hex }}
                      title={color.name}
                    >
                      {formData.color === color.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Check className={cn(
                            "w-8 h-8",
                            ["branco", "amarelo"].includes(color.id) ? "text-gray-800" : "text-white"
                          )} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </Card>

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={handleBack} className="text-white hover:bg-white/20">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <Button onClick={handleNext} disabled={!canProceed()} className="bg-white text-primary hover:bg-white/90">
                  Próximo Passo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Symbol Concept - Layout matches Step 2 */}
          {currentStep === 4 && (
            <div className="max-w-6xl mx-auto animate-fade-in">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-white mb-2 sm:mb-3 text-balance leading-tight tracking-tight break-words">
                  Qual caminho visual devemos seguir?
                </h2>
                <p className="text-white/80 text-base sm:text-lg text-balance leading-relaxed">
                  Baseado na sua descrição, escolha um conceito.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {SYMBOL_CONCEPTS.map((concept) => (
                  <Card
                    key={concept.id}
                    onClick={() => updateFormData("symbolConcept", concept.id)}
                    className={cn(
                      "p-5 md:p-6 cursor-pointer transition-all duration-200 hover:shadow-lg bg-white/95 backdrop-blur h-full flex items-center",
                      formData.symbolConcept === concept.id
                        ? "bg-primary ring-2 ring-primary scale-[1.02]"
                        : "hover:border-primary/50"
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                        formData.symbolConcept === concept.id 
                          ? "border-white bg-white" 
                          : "border-muted-foreground/30"
                      )}>
                        {formData.symbolConcept === concept.id && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <div>
                        <h3 className={cn(
                          "text-lg md:text-xl font-semibold",
                          formData.symbolConcept === concept.id ? "text-white" : "text-foreground"
                        )}>
                          {concept.title}
                        </h3>
                        <p className={cn(
                          "text-sm",
                          formData.symbolConcept === concept.id ? "text-white/80" : "text-muted-foreground"
                        )}>
                          {concept.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* AI-powered refinement input */}
              {formData.symbolConcept && (
                <Card className="mt-4 p-4 bg-white/95 backdrop-blur">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">
                      Refine sua ideia:
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchAiSuggestion}
                      disabled={isLoadingSuggestion}
                      className="text-primary hover:text-primary/80 h-8 px-2"
                    >
                      {isLoadingSuggestion ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1" />
                          Sugerir ideia
                        </>
                      )}
                    </Button>
                  </div>
                  <Textarea
                    placeholder="Descreva sua ideia ou deixe a IA criar algo único para você..."
                    value={formData.conceptRefinement}
                    onChange={(e) => updateFormData("conceptRefinement", e.target.value)}
                    className="border-dashed min-h-[80px]"
                  />
                  {isLoadingSuggestion && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Gerando sugestão criativa...
                    </p>
                  )}
                </Card>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={handleBack} className="text-white hover:bg-white/20">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <Button 
                  onClick={handleNext} 
                  disabled={!canProceed() || isGenerating} 
                  className="bg-white text-primary hover:bg-white/90 min-h-[44px]"
                >
                  {isGenerating ? (
                    <LoadingMessages />
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Gerar Minha Marca
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Logo Approval (NEW - Paywall Checkpoint) */}
          {currentStep === 5 && rawLogoUrl && (
            <div className="max-w-5xl mx-auto animate-fade-in px-6 sm:px-8">
              <LogoApprovalStep
                rawLogoUrl={rawLogoUrl}
                brandName={formData.brandName}
                attemptNumber={generationCount}
                maxAttempts={MAX_GENERATIONS}
                isProcessing={isFinalizingBrand || isGenerating}
                onApprove={() => handleApprovalAction('approve')}
                onRegenerate={() => handleApprovalAction('regenerate')}
                onBack={handleBack}
              />
            </div>
          )}

          {/* Step 6: Results (Final Dashboard) */}
          {currentStep === 6 && (
            <div className="w-full max-w-5xl mx-auto animate-fade-in px-4 sm:px-6">
              {/* Celebration Header - Compact */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-3">
                  <span className="text-xl">🎉</span>
                  <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">Marca Criada</span>
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-white mb-1.5 text-balance leading-tight tracking-tight break-words">
                  Sua marca está pronta!
                </h2>
                <p className="text-sm text-white/70 break-words max-w-full">
                  Baixe a identidade visual de <span className="font-semibold break-all">{formData.brandName}</span>
                </p>
              </div>

              {/* BENTO GRID - 2x2 Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
                
                {/* Card 1: Logo Hero + Download Integrated */}
                <Card 
                  className="bg-gradient-to-br from-white via-slate-50 to-slate-100 p-0 overflow-hidden relative select-none shadow-2xl shadow-black/10 ring-1 ring-black/5 rounded-3xl"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {/* Logo Image */}
                  <div className="aspect-square w-full flex items-center justify-center">
                    {(heroPreviewUrl || generatedBrand?.logo?.url) ? (
                      <img
                        src={heroPreviewUrl || generatedBrand?.logo?.url}
                        alt={formData.brandName}
                        className="w-full h-full object-contain p-8 select-none pointer-events-none"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ 
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitUserDrag: 'none'
                        } as React.CSSProperties}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <Sparkles className="w-16 h-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Download Button - Integrated at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 via-black/40 to-transparent">
                    <Button 
                      size="lg" 
                      className="w-full h-12 text-base gap-2 bg-white text-foreground hover:bg-white/90 shadow-lg"
                      onClick={handleDownload}
                    >
                      <Download className="w-5 h-5 flex-shrink-0" />
                      <span className="truncate">Baixar Logo (PNG)</span>
                    </Button>
                  </div>
                </Card>

                {/* Card 2: Typography */}
                <Card className="p-5 lg:p-6 bg-gradient-to-br from-white to-slate-50/80 shadow-xl shadow-black/5 border-0 ring-1 ring-black/5 rounded-3xl flex flex-col">
                  {/* Typography Section */}
                  <div className="flex-1 flex flex-col">
                    <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-3">
                      Tipografia
                    </h3>
                    
                    {/* Aplicação Prática - Type Specimen */}
                    <div className="bg-slate-50/70 rounded-2xl p-4 lg:p-5 mb-4 ring-1 ring-black/5 flex-1">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-4">Aplicação Prática</p>
                      
                      {/* Nome da Marca - Fonte Display (GIGANTE) */}
                      <div className="mb-3">
                        <h2 
                          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground break-words [text-wrap:balance] leading-tight"
                          style={{ fontFamily: `'${generatedBrand?.fontKit?.display?.name || 'Satoshi'}', sans-serif` }}
                        >
                          {formData.brandName}
                        </h2>
                        <a 
                          href={getFontDownloadLink(generatedBrand?.fontKit?.display?.name || "Satoshi")} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[9px] text-muted-foreground hover:text-primary transition-colors mt-1"
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          Fonte: {generatedBrand?.fontKit?.display?.name || 'Satoshi'}
                        </a>
                      </div>
                      
                      {/* Título exemplo - Fonte Títulos (Médio) */}
                      <h3 
                        className="text-base lg:text-lg font-semibold text-foreground mb-2 break-words [text-wrap:balance]"
                        style={{ fontFamily: `'${generatedBrand?.fontKit?.titles?.name || 'Switzer'}', sans-serif` }}
                      >
                        "{generatedBrand?.copywriting?.tagline || `A excelência da ${formData.brandName}`}"
                      </h3>
                      
                      {/* Bio exemplo - Fonte Corpo (Pequeno) */}
                      <p 
                        className="text-sm text-muted-foreground leading-relaxed break-words line-clamp-3"
                        style={{ fontFamily: `'${generatedBrand?.fontKit?.body?.name || 'General Sans'}', sans-serif` }}
                      >
                        {generatedBrand?.copywriting?.bio || `Soluções profissionais em ${formData.segment}. Entre em contato!`}
                      </p>
                    </div>
                    
                    {/* Font Cards - Mini Row (apenas Títulos e Corpo) */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Heading */}
                      <div className="bg-slate-50 rounded-xl p-3 text-center ring-1 ring-black/5 hover:shadow-md transition-all duration-300">
                        <p className="text-[8px] text-muted-foreground uppercase tracking-wider mb-1">Títulos</p>
                        <p 
                          className="text-xl font-semibold text-foreground"
                          style={{ fontFamily: `'${generatedBrand?.fontKit?.titles?.name || 'Switzer'}', sans-serif` }}
                        >
                          Aa
                        </p>
                        <a 
                          href={getFontDownloadLink(generatedBrand?.fontKit?.titles?.name || "Switzer")} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[9px] text-primary hover:underline font-medium mt-1.5"
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          Baixar
                        </a>
                      </div>
                      
                      {/* Body */}
                      <div className="bg-slate-50 rounded-xl p-3 text-center ring-1 ring-black/5 hover:shadow-md transition-all duration-300">
                        <p className="text-[8px] text-muted-foreground uppercase tracking-wider mb-1">Corpo</p>
                        <p 
                          className="text-xl text-foreground"
                          style={{ fontFamily: `'${generatedBrand?.fontKit?.body?.name || 'General Sans'}', sans-serif` }}
                        >
                          Aa
                        </p>
                        <a 
                          href={getFontDownloadLink(generatedBrand?.fontKit?.body?.name || "General Sans")} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-[9px] text-primary hover:underline font-medium mt-1.5"
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          Baixar
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  {/* Generation counter badge - only for paid users */}
                  {hasPurchased && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full text-xs text-primary mt-4 self-start">
                      <Sparkles className="w-3 h-3" />
                      <span>Geração {generationCount}/{MAX_GENERATIONS}</span>
                    </div>
                  )}
                </Card>

                {/* Card 3: Color Palette - Prominent Swatches */}
                <Card className="p-5 lg:p-6 bg-gradient-to-br from-white to-slate-50/80 shadow-xl shadow-black/5 border-0 ring-1 ring-black/5 rounded-3xl flex flex-col min-h-[320px] lg:min-h-[380px]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      Paleta de Cores
                    </h3>
                    <span className="text-[9px] text-muted-foreground">Clique para copiar</span>
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:gap-6 place-items-center">
                      {generatedBrand?.colorPalette?.map((color) => (
                        <ColorSwatch
                          key={color.name}
                          hex={color.hex}
                          name={color.name}
                          showCmyk={false}
                          size="responsive"
                        />
                      ))}
                    </div>
                  </div>
                </Card>

                {/* Card 4: Variations Preview - Compact Grid */}
                <Card className="p-5 lg:p-6 bg-gradient-to-br from-white to-slate-50/80 shadow-xl shadow-black/5 border-0 ring-1 ring-black/5 rounded-3xl">
                  <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mb-4">
                    <Layers className="w-3 h-3" />
                    Variações (1080×1080px)
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {getLogoVariations().map((variation) => (
                      <div 
                        key={variation.id}
                        className={cn(
                          "rounded-xl overflow-hidden ring-1 ring-black/5 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 select-none",
                          variation.checkerboard && "bg-[repeating-conic-gradient(#d1d5db_0%_25%,#f3f4f6_0%_50%)] bg-[length:10px_10px]"
                        )}
                        style={!variation.checkerboard ? { backgroundColor: variation.bgColor } : undefined}
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        {/* Image */}
                        <div className="aspect-square w-full overflow-hidden relative">
                          {variation.previewUrl && (
                            <img 
                              src={variation.previewUrl}
                              alt={variation.label} 
                              className="w-full h-full object-cover block select-none pointer-events-none"
                              draggable={false}
                              onContextMenu={(e) => e.preventDefault()}
                              style={{ 
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                WebkitUserDrag: 'none'
                              } as React.CSSProperties}
                            />
                          )}
                        </div>
                        
                        {/* Label + Download */}
                        <div className={cn(
                          "flex items-center justify-between px-2.5 py-2",
                          variation.id === 'tons-neutros' 
                            ? "bg-black/30 text-white" 
                            : "bg-white"
                        )}>
                          <span className="text-[9px] font-medium truncate">{variation.label}</span>
                          <button 
                            onClick={() => handleVariationDownload(variation)}
                            className={cn(
                              "p-1 rounded-md transition-colors",
                              variation.id === 'tons-neutros' 
                                ? "hover:bg-white/20 text-white" 
                                : "hover:bg-slate-100 text-foreground"
                            )}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

{/* CTA Actions - Dashboard + Mascote Cross-sell */}
                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-8">
                  {/* Botão A: Ir para Dashboard (Secundário) */}
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={() => navigate("/dashboard")}
                    className="w-full sm:w-auto gap-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <Folder className="w-4 h-4" />
                    Ver Meus Projetos
                  </Button>
                  
                  {/* Botão B: Criar Mascote (Destaque) */}
                  <Button 
                    size="lg"
                    onClick={handleClaimFreeMascot}
                    className="w-full sm:w-auto gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-orange-500/25"
                  >
                    <Gift className="w-4 h-4" />
                    Criar Mascote
                    <span className="ml-1 bg-white/20 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                      BÔNUS
                    </span>
                  </Button>
                </div>

                {/* Informativo de valor */}
                <p className="text-center text-white/80 text-xs mt-3">
                  🎁 Você ganhou 1 mascote de bônus por ter criado sua marca!
                </p>
            </div>
          )}

          {/* Hard Paywall Modal - Blocks regeneration without payment */}
          <HardPaywallModal 
            open={showHardPaywall} 
            onLogin={handleLoginFromHardPaywall}
          />
        </div>
      </section>

      {/* TRUST BAR - Social Proof Strip */}
      {currentStep === 1 && (
        <section className="py-6 sm:py-8 bg-muted/50 border-y border-border/50">
          <div className="container mx-auto text-center px-6 sm:px-8">
            <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
              A IA escolhida por empreendedores modernos
            </p>
            <div className="flex items-center justify-center gap-2 text-foreground/80">
              <span className="text-base sm:text-xl">🔥</span>
              <span className="font-medium text-sm sm:text-base">
                Mais de 5.000 artes geradas esta semana
              </span>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 2: O QUE VOCÊ RECEBE - Horizontal Carousel on Mobile */}
      {currentStep === 1 && (
        <section className="py-12 sm:py-16 lg:py-24 px-6 sm:px-8 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-8 sm:mb-10 lg:mb-14">
              <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-2 sm:mb-3 text-balance leading-snug tracking-tight">O que você recebe</h2>
              <p className="text-sm sm:text-base text-foreground/70">
                Tudo pronto para usar em qualquer lugar
              </p>
            </div>
            
            {/* Horizontal scroll carousel on mobile, grid on desktop */}
            <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-6 pl-4 pr-16 sm:grid sm:grid-cols-2 md:grid-cols-4 sm:gap-5 md:gap-8 sm:overflow-visible sm:px-0 sm:pl-0 sm:pr-0 scrollbar-hide">
              <div className="flex-shrink-0 w-[70vw] max-w-[220px] sm:w-auto sm:max-w-none text-center p-4 sm:p-5 md:p-6 snap-center sm:snap-start bg-card/80 backdrop-blur-sm rounded-2xl border border-border card-feature reveal-on-scroll" style={{ transitionDelay: '0ms' }}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 mx-auto mb-3 sm:mb-4 md:mb-5 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Download className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-sm sm:text-base md:text-lg mb-1.5 sm:mb-2">Alta Resolução</h3>
                <p className="text-xs sm:text-sm text-foreground/60">Arquivos PNG prontos para&nbsp;impressão</p>
              </div>
              
              <div className="flex-shrink-0 w-[70vw] max-w-[220px] sm:w-auto sm:max-w-none text-center p-4 sm:p-5 md:p-6 snap-center sm:snap-start bg-card/80 backdrop-blur-sm rounded-2xl border border-border card-feature reveal-on-scroll" style={{ transitionDelay: '100ms' }}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 mx-auto mb-3 sm:mb-4 md:mb-5 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Layers className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-sm sm:text-base md:text-lg mb-1.5 sm:mb-2">Fundo Transparente</h3>
                <p className="text-xs sm:text-sm text-foreground/60">Use em qualquer material</p>
              </div>
              
              <div className="flex-shrink-0 w-[70vw] max-w-[220px] sm:w-auto sm:max-w-none text-center p-4 sm:p-5 md:p-6 snap-center sm:snap-start bg-card/80 backdrop-blur-sm rounded-2xl border border-border card-feature reveal-on-scroll" style={{ transitionDelay: '200ms' }}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 mx-auto mb-3 sm:mb-4 md:mb-5 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Droplets className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-sm sm:text-base md:text-lg mb-1.5 sm:mb-2">Paleta de Cores</h3>
                <p className="text-xs sm:text-sm text-foreground/60">4 cores harmonizadas</p>
              </div>
              
              <div className="flex-shrink-0 w-[70vw] max-w-[220px] sm:w-auto sm:max-w-none text-center p-4 sm:p-5 md:p-6 snap-center sm:snap-start bg-card/80 backdrop-blur-sm rounded-2xl border border-border card-feature reveal-on-scroll" style={{ transitionDelay: '300ms' }}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 mx-auto mb-3 sm:mb-4 md:mb-5 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Type className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-sm sm:text-base md:text-lg mb-1.5 sm:mb-2">Tipografia</h3>
                <p className="text-xs sm:text-sm text-foreground/60">Fontes comerciais gratuitas selecionadas</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 3: PRODUCTS - "O que você quer criar hoje?" */}
      {currentStep === 1 && (
        <section className="py-12 sm:py-16 lg:py-24 px-6 sm:px-8 pb-24 sm:pb-20 lg:pb-24 bg-background">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-center mb-8 sm:mb-10 lg:mb-14 text-balance leading-snug tracking-tight">
              O que você quer criar hoje?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Card 1: Identidade Visual - With Preview */}
              <Card 
                className="order-2 md:order-1 p-0 overflow-hidden shadow-sm hover:shadow-primary-lg transition-all duration-300 cursor-pointer border-2 border-border hover:border-primary hover:-translate-y-1 group reveal-on-scroll"
                style={{ transitionDelay: '0ms' }}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => {
                    document.querySelector<HTMLInputElement>('input[placeholder*="nome"]')?.focus();
                  }, 500);
                }}
              >
                <div className="relative h-48 bg-muted/50 overflow-hidden">
                  <img 
                    src={brandGuidelinesPreview}
                    alt="Preview de Identidade Visual"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="592"
                    height="192"
                    decoding="async"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <Zap className="w-3 h-3" /> IA Instantânea
                    </span>
                  </div>
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="heading-card mb-3">Identidade Visual Completa</h3>
                  <p className="text-foreground/60 text-sm mb-5">
                    Logo profissional, paleta de cores e tipografia em segundos.
                  </p>
                  <Button variant="outline" className="w-full bg-white hover:bg-primary hover:text-white hover:border-primary transition-all">
                    Criar Minha Marca ↑
                  </Button>
                </div>
              </Card>

              {/* Card 2: Mascote Publicitário - PRIMEIRO no mobile */}
              <Card 
                className="order-1 md:order-2 p-0 overflow-hidden shadow-sm hover:shadow-primary-lg transition-all duration-300 cursor-pointer border-2 border-border hover:border-primary hover:-translate-y-1 group reveal-on-scroll"
                style={{ transitionDelay: '150ms' }}
                onClick={() => navigate('/mascote-generator')}
              >
                <div className="relative h-48 bg-muted/50 overflow-hidden">
                  <img 
                    src={mascotesPreview}
                    alt="Exemplos de Mascotes 3D e Cartoon"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="592"
                    height="192"
                    decoding="async"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <Zap className="w-3 h-3" /> IA Instantânea
                    </span>
                  </div>
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="heading-card mb-3">Mascote Publicitário</h3>
                  <p className="text-foreground/60 text-sm mb-5">
                    Mascotes aumentam o engajamento em até 3x. Crie um personagem único que é a cara do seu negócio.
                  </p>
                  <Button variant="outline" className="w-full bg-white hover:bg-primary hover:text-white hover:border-primary transition-all">
                    Dar Vida ao Meu Mascote
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* LEGACY CONTENT - BELOW THE FOLD */}
      {currentStep === 1 && (
        <>
          {/* Social Proof */}
          <LogoGallery />
          <SocialProof />

          {/* Pricing Section */}
          <section className="py-16 sm:py-20 lg:py-28 px-5 sm:px-6 bg-muted/30">
            <div className="container mx-auto">
              <div className="text-center mb-10 sm:mb-14 lg:mb-16">
                <h2 className="heading-section mb-4 text-balance">Escolha seu plano</h2>
                <p className="subtitle-section">
                  Sem mensalidades escondidas, sem surpresas
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto items-center">
                {plans.map((plan, index) => (
                  <Card key={index} className={cn("p-6 sm:p-8 relative transition-all duration-300 hover:-translate-y-1", plan.popular ? "border-2 border-primary shadow-glow md:scale-105 z-10" : "hover:shadow-primary")}>

                    {plan.badge && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                          {plan.badge}
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-6 mt-2">
                      <h3 className="text-2xl font-bold mb-3">{plan.name}</h3>
                      {plan.originalPrice && (
                        <div className="text-lg text-muted-foreground line-through mb-1">
                          {plan.originalPrice}
                        </div>
                      )}
                      <div className="flex items-baseline justify-center gap-2 mb-2">
                        <span className="text-4xl font-bold">{plan.price}</span>
                        {plan.period && <span className="text-muted-foreground">/{plan.period}</span>}
                      </div>
                      {plan.discount && (
                        <div className="inline-block bg-destructive text-destructive-foreground px-3 py-1 rounded-full text-sm font-bold">
                          {plan.discount}
                        </div>
                      )}
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {'bonus' in plan && plan.bonus && (
                      <div className="flex items-start gap-2 mb-6 p-3 bg-accent/10 rounded-lg">
                        <Gift className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                        <span className="text-sm font-semibold text-accent">{plan.bonus}</span>
                      </div>
                    )}

                    <Button 
                      className="w-full" 
                      variant={plan.popular ? "default" : "outline"} 
                      size="lg" 
                      disabled={!plan.available}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {plan.buttonText}
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <FAQ />

          {/* Footer */}
          <Footer />

          {/* WhatsApp Button */}
          <a
            href="https://wa.me/5511999999999?text=Ol%C3%A1%2C%20vim%20pelo%20ClickLogos%20e%20gostaria%20de%20saber%20mais!"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg transition-colors"
            aria-label="Fale conosco no WhatsApp"
          >
            <MessageCircle className="w-6 h-6" />
          </a>
        </>
      )}

      {/* Signup Modal */}
      <SignupModal 
        open={showSignupModal} 
        onOpenChange={setShowSignupModal}
        brandName={formData.brandName}
        onBeforeOAuthRedirect={handleBeforeOAuthRedirect}
        onSuccess={() => {
          // After successful login/signup, refetch purchase status
          refetchStatus();
          
          // 🆕 Execute pending approval action if any
          if (pendingApprovalAction) {
            const action = pendingApprovalAction;
            setPendingApprovalAction(null);
            // Small delay to allow session to be established
            setTimeout(() => {
              handleApprovalAction(action);
            }, 500);
          }
        }}
      />

      {/* Paywall Modal - For logged in users who haven't paid */}
      <PaywallModal
        open={showPaywall}
        productType="brand_kit"
        previewImageUrl={rawLogoUrl || generatedBrand?.logo?.url}
        onPaymentSuccess={() => {
          setShowPaywall(false);
          refetchStatus();
          toast.success("Pagamento confirmado! 🎉");
          
          // 🆕 Execute pending approval action if any
          if (pendingApprovalAction) {
            const action = pendingApprovalAction;
            setPendingApprovalAction(null);
            // Small delay to allow session to be established
            setTimeout(() => {
              handleApprovalAction(action);
            }, 500);
          } else if (pendingDownload) {
            // Execute pending download using CLEAN URL (no watermark)
            executeActualDownload(pendingDownload.cleanUrl, pendingDownload.id, pendingDownload.bgColor);
            setPendingDownload(null);
          } else {
            // Hero logo download was pending - use CLEAN URL
            executeActualDownload(heroCleanUrl || generatedBrand?.logo?.url, 'logo');
          }
        }}
      />
    </main>
  );
};

export default Index;
